<!-- OrderForecast.vue -->
<script setup lang="ts">
import { LineChart } from "vue-chart-3";
import { Chart, registerables } from "chart.js";
import { computed, onMounted, ref } from "vue";

// Register Chart.js components
Chart.register(...registerables);

// Props
interface Props {
  height?: number;
  styles?: Record<string, string>;
}

const props = withDefaults(defineProps<Props>(), {
  height: 150,
  styles: () => ({
    "margin-left": "0px",
    background: "#a5b8a7",
  }),
});

// Refs for gradients (need canvas context)
const gradient = ref<string | CanvasGradient>("");
const gradient2 = ref<string | CanvasGradient>("");

// Chart data
const chartData = computed(() => ({
  labels: ["January", "February", "March", "April", "May", "June", "July"],
  datasets: [
    {
      label: "Data One",
      borderColor: "#FC2525",
      pointBackgroundColor: "white",
      borderWidth: 1,
      pointBorderColor: "white",
      backgroundColor: gradient.value,
      data: [40, 39, 10, 40, 39, 80, 40],
      tension: 0.4, // Makes line smoother (optional)
      fill: true, // Fill area under line
    },
    {
      label: "Data Two",
      borderColor: "#05CBE1",
      pointBackgroundColor: "white",
      pointBorderColor: "white",
      borderWidth: 1,
      backgroundColor: gradient2.value,
      data: [60, 55, 32, 10, 2, 12, 53],
      tension: 0.4, // Makes line smoother (optional)
      fill: true, // Fill area under line
    },
  ],
}));

// Chart options for Chart.js v3
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: {
      display: true,
      text: "Order Forecast",
    },
    legend: {
      display: false,
    },
    tooltip: {
      mode: "index" as const,
      intersect: false,
    },
  },
  scales: {
    x: {
      display: false,
      grid: {
        display: false,
      },
      ticks: {
        display: false,
      },
    },
    y: {
      display: false,
      grid: {
        display: false,
      },
      ticks: {
        display: false,
      },
    },
  },
  elements: {
    point: {
      radius: 4, // Point size
      hoverRadius: 6, // Point size on hover
    },
    line: {
      tension: 0.4, // Line smoothness
    },
  },
  interaction: {
    intersect: false,
    mode: "nearest" as const,
  },
}));

// Container styles
const containerStyle = computed(() => ({
  height: `${props.height}px`,
  ...props.styles,
}));

// Create gradients when component mounts
const createGradients = (ctx: CanvasRenderingContext2D) => {
  if (!ctx) return;

  // Gradient for first dataset
  const gradient1 = ctx.createLinearGradient(0, 0, 0, 400);
  gradient1.addColorStop(0, "rgba(252, 37, 37, 0.8)"); // #FC2525 with opacity
  gradient1.addColorStop(1, "rgba(252, 37, 37, 0.1)");

  // Gradient for second dataset
  const gradient2Fill = ctx.createLinearGradient(0, 0, 0, 400);
  gradient2Fill.addColorStop(0, "rgba(5, 203, 225, 0.8)"); // #05CBE1 with opacity
  gradient2Fill.addColorStop(1, "rgba(5, 203, 225, 0.1)");

  gradient.value = gradient1;
  gradient2.value = gradient2Fill;
};

// Alternative: Simple colors without gradients
const useSimpleColors = () => {
  gradient.value = "rgba(252, 37, 37, 0.3)";
  gradient2.value = "rgba(5, 203, 225, 0.3)";
};

// Initialize
useSimpleColors(); // Use simple colors by default

// If you need canvas context for gradients, you can use a chart ref
const chartRef = ref();

// Expose methods if needed
defineExpose({
  updateChart: (newData: { dataset1: number[]; dataset2: number[] }) => {
    // Update chart data logic
    console.log("Update chart data:", newData);
  },
});
</script>

<template>
  <div :style="containerStyle">
    <LineChart ref="chartRef" :chart-data="chartData" :options="chartOptions" />
  </div>
</template>
